import {
  Configuration,
  PublicClientApplication,
  InteractionRequiredAuthError,
  type AccountInfo,
} from '@azure/msal-browser';

export const authDisabled = import.meta.env.VITE_AUTH_DISABLED === 'true';
export const apiScope = import.meta.env.VITE_API_SCOPE as string | undefined;

const msalConfig: Configuration = {
  auth: {
    clientId: (import.meta.env.VITE_ENTRA_CLIENT_ID as string) ?? '',
    authority: `https://login.microsoftonline.com/${
      (import.meta.env.VITE_ENTRA_TENANT_ID as string) ?? 'common'
    }`,
    redirectUri: window.location.origin,
    postLogoutRedirectUri: window.location.origin,
  },
  // localStorage so the sign-in survives tab reloads/new tabs.
  cache: { cacheLocation: 'localStorage', storeAuthStateInCookie: false },
};

export const msalInstance = new PublicClientApplication(msalConfig);

export const loginRequest = {
  scopes: apiScope ? [apiScope] : ['openid', 'profile'],
};

/**
 * Remove a leftover MSAL "interaction in progress" flag. An interrupted redirect
 * login otherwise leaves this stuck in localStorage (survives hard refresh) and
 * makes MSAL refuse new interactions — the app then hangs/blanks.
 */
export function clearStuckInteraction(): void {
  try {
    Object.keys(localStorage)
      .filter((k) => /interaction\.status/i.test(k))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* ignore */
  }
}

// Guards against firing multiple concurrent redirects within one page load. Resets
// naturally on the next load (module re-evaluated) — intentionally NOT persisted, so
// a transient failure can never trap the session in a permanent no-token state.
let interactionInFlight = false;

// --- "never a silent blank" safety net -------------------------------------
// If auth can't recover after a few redirect attempts, we surface a visible
// "Sign in" overlay (subscribers below) instead of leaving the app blank.
type AuthFailedCb = () => void;
const authFailedCbs = new Set<AuthFailedCb>();
export function onAuthFailed(cb: AuthFailedCb): () => void {
  authFailedCbs.add(cb);
  return () => authFailedCbs.delete(cb);
}
function notifyAuthFailed(): void {
  authFailedCbs.forEach((cb) => {
    try {
      cb();
    } catch {
      /* ignore */
    }
  });
}

/** True if we've bounced through sign-in too many times in a short window — i.e.
 *  re-auth isn't fixing it, so stop redirecting and show the manual overlay. */
function redirectStorm(): boolean {
  try {
    const now = Date.now();
    const recent = (JSON.parse(sessionStorage.getItem('auth.redirects') || '[]') as number[]).filter(
      (t) => now - t < 60000,
    );
    recent.push(now);
    sessionStorage.setItem('auth.redirects', JSON.stringify(recent));
    return recent.length > 3;
  } catch {
    return false;
  }
}

/** Full clean re-login for the "Sign in" overlay button: wipe stuck flags and start fresh. */
export function forceReLogin(): void {
  try {
    sessionStorage.removeItem('auth.redirects');
  } catch {
    /* ignore */
  }
  clearStuckInteraction();
  interactionInFlight = false;
  msalInstance.loginRedirect(loginRequest).catch((e) => {
    // eslint-disable-next-line no-console
    console.error('[auth] loginRedirect failed', e);
  });
}

/** Send the user through interactive sign-in via REDIRECT (popups get blocked). */
export async function reauth(account?: AccountInfo): Promise<null> {
  if (interactionInFlight) return null;
  // If re-auth keeps looping, don't storm — show the visible recovery overlay instead.
  if (redirectStorm()) {
    notifyAuthFailed();
    return null;
  }
  interactionInFlight = true;
  try {
    const acct =
      account ?? msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? undefined;
    await msalInstance.acquireTokenRedirect({ ...loginRequest, account: acct });
  } catch (e) {
    interactionInFlight = false;
    clearStuckInteraction();
    notifyAuthFailed();
    // eslint-disable-next-line no-console
    console.error('[auth] acquireTokenRedirect failed', e);
  }
  return null;
}

/** Access token for the API (silent; interactive redirect if the session expired). */
export async function getAccessToken(): Promise<string | null> {
  if (authDisabled) return null;
  const account: AccountInfo | null =
    msalInstance.getActiveAccount() ?? msalInstance.getAllAccounts()[0] ?? null;
  if (!account) return reauth();
  try {
    const res = await msalInstance.acquireTokenSilent({ ...loginRequest, account });
    return res.accessToken;
  } catch (e) {
    if (!(e instanceof InteractionRequiredAuthError)) {
      // eslint-disable-next-line no-console
      console.error('[auth] acquireTokenSilent failed', e);
    }
    return reauth(account);
  }
}
